/**
 * Shared design tokens for the Saqia irrigation management platform.
 * Derived from the Sagia Foundations Figma file.
 * Used by both light and dark themes.
 */

/* ── Primitive palette (Figma → cvg-color-guide) ── */

export const palette = {
  primary: {
    50:  '#e7f2f0',
    100: '#b5d7d0',
    200: '#92c4b9',
    300: '#60a999',
    400: '#419885',
    500: '#117e67',
    600: '#0f735e',
    700: '#0c5949',
    800: '#094539',
    900: '#00281d',
  },
  secondary: {
    50:  '#e7fbf7',
    100: '#b3f4e6',
    200: '#8feeda',
    300: '#5ce7c9',
    400: '#3ce2be',
    500: '#0bdbae',
    600: '#0ac79e',
    700: '#089b7c',
    800: '#067860',
    900: '#055c49',
  },
  neutral: {
    40:   '#ffffff',
    50:   '#f8f8f8',
    100:  '#e8e9e8',
    200:  '#dddedd',
    300:  '#cdcfcd',
    400:  '#c4c5c3',
    500:  '#b5b7b4',
    600:  '#a5a7a4',
    700:  '#818280',
    800:  '#646563',
    900:  '#4c4d4c',
    1000: '#000000',
  },
  feedback: {
    success: { 100: '#dff5ea', 500: '#3cb179', 700: '#185638' },
    warning: { 100: '#fff4d6', 500: '#ffc247', 700: '#996000' },
    error:   { 100: '#ffe2e0', 500: '#e5484d', 700: '#b33136' },
  },
  focus: '#2563ff',
} as const;

/* ── Semantic tokens ── */

export const tokens = {
  colors: {
    primary: palette.primary[500],
    primaryDark: palette.primary[700],
    primaryLight: palette.primary[400],
    textPrimary: palette.primary[900],
    textSecondary: palette.neutral[900],
    surfaceOnTop: palette.neutral[50],
    surfaceDefault: palette.neutral[40],
    border: palette.neutral[300],
    sidebar: {
      bg: palette.primary[900],
      bgHover: palette.primary[800],
      bgActive: palette.primary[800],
      text: '#ffffff',
      textActive: palette.secondary[500],
      textMuted: 'rgba(255, 255, 255, 0.6)',
      sectionLabel: palette.primary[100],
    },
    success: palette.feedback.success[500],
    warning: palette.feedback.warning[500],
    error: palette.feedback.error[500],
    info: palette.focus,
  },
  layout: {
    sidebarWidth: 240,
    topBarHeight: 64,
    topBarHeightMobile: 56,
  },
  /** Figma Grid Layout — breakpoints, columns, gutter & padding per tier */
  grid: {
    breakpoints: { xs: 0, sm: 600, md: 768, lg: 1024, xl: 1280 },
    columns:     { xs: 4, sm: 4, md: 6, lg: 12, xl: 12 },
    gutter:      { xs: 16, sm: 16, md: 16, lg: 24, xl: 24 },
    padding:     { xs: 16, sm: 16, md: 24, lg: 40, xl: 40 },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
    borderRadiusSmall: 8,
    borderRadiusChip: 16,
  },
} as const;
